import React from 'react';
import { ArrowRight } from 'lucide-react';
import { ExamCatalogEntry } from '../data/examCatalog';
import { FamilyArt, getFamilyTheme } from './brand/FamilyArt';

// One illustrated exam-family card (2026-10-07 navy redesign), shared by
// the front page and the logged-in Exams Directory so both look the same.
// A family with no practice content yet is shown faded as "Coming soon"
// and is not clickable, instead of opening an empty page.

interface ExamFamilyCardProps {
  entry: ExamCatalogEntry;
  paperCount: number;
  isGoal?: boolean;
  tagLabel?: string;
  showPills?: boolean;
  onOpen: (entry: ExamCatalogEntry) => void;
}

export const ExamFamilyCard: React.FC<ExamFamilyCardProps> = ({
  entry,
  paperCount,
  isGoal = false,
  tagLabel,
  showPills = false,
  onOpen
}) => {
  const theme = getFamilyTheme(entry);
  const live = paperCount > 0;
  const pills = entry.id === 'most-other-states'
    ? ['Core', 'Elementary', 'PLT', 'Subject']
    : entry.id === 'paraprofessional-pathway'
    ? ['5758', '5759']
    : entry.examCodes;

  const body = (
    <>
      <div className="relative h-[84px] overflow-hidden" style={{ background: theme.bg }}>
        {isGoal ? (
          <span className="absolute left-3 top-2.5 z-10 text-[10px] font-extrabold rounded-full px-2.5 py-0.5 bg-emerald-500 text-white">
            ★ Your goal
          </span>
        ) : !live ? (
          <span className="absolute left-3 top-2.5 z-10 text-[10px] font-extrabold rounded-full px-2.5 py-0.5 bg-white text-slate-500 border border-slate-200">
            Coming soon
          </span>
        ) : tagLabel ? (
          <span className="absolute left-3 top-2.5 z-10 text-[10px] font-extrabold rounded-full px-2.5 py-0.5 bg-[#0f1b4d] text-white">
            {tagLabel}
          </span>
        ) : null}
        <FamilyArt entry={entry} className="absolute right-1.5 bottom-0 block w-[150px] h-[66px] [&>svg]:w-full [&>svg]:h-full" />
        <div
          className="absolute left-3.5 bottom-2.5 w-10 h-10 rounded-full border-[3px] border-white text-white font-black text-[13px] flex items-center justify-center shadow-md"
          style={{ background: theme.dot }}
        >
          {theme.code}
        </div>
      </div>
      <div className="px-3.5 pt-3 pb-3 flex-1 flex flex-col text-left">
        <h4 className="text-[15px] font-extrabold text-[#0f1b4d] leading-snug">{entry.state}</h4>
        <p className="text-[11.5px] text-slate-500 leading-snug mt-0.5 min-h-[30px]">{theme.blurb || entry.examLabel}</p>
        {showPills && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {pills.map((p) => (
              <span key={p} className="text-[10.5px] font-extrabold bg-slate-50 border border-slate-200 text-slate-700 rounded-md px-1.5 py-0.5">
                {p}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-2.5 border-t border-slate-100 flex items-center text-xs font-extrabold" style={{ marginTop: 10 }}>
          <span className={live ? 'text-slate-700' : 'text-slate-400'}>
            {live ? `${paperCount} paper${paperCount === 1 ? '' : 's'}` : 'In preparation'}
          </span>
          {live && (
            <span className="ml-auto text-[#0E9594] flex items-center gap-1">
              View exams <ArrowRight className="w-3.5 h-3.5" />
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (!live) {
    return (
      <div className="bg-slate-50/70 border border-slate-200 rounded-2xl overflow-hidden flex flex-col saturate-[.8]" aria-disabled="true">
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(entry)}
      className={`bg-white border rounded-2xl overflow-hidden flex flex-col shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition cursor-pointer ${
        isGoal ? 'border-emerald-500 ring-2 ring-emerald-400/60' : 'border-slate-200 hover:border-[#0E9594]/60'
      }`}
    >
      {body}
    </button>
  );
};
